/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useSelector } from "react-redux";

import styled from "styled-components";
import { format as tzFormat } from "date-fns-tz";
import { addMinutes, differenceInMinutes } from "date-fns";

import {
  formatTimeWithTimeZone,
  utcToZonedDateTime,
} from "../../helpers/dateHelpers";
import types from "../../helpers/types";

import { useTheme } from "@material-ui/core/styles";
import { List, Grid, Tooltip } from "@material-ui/core";
import SuperscriptText from "../common/SuperscriptText";

const StyledList = styled(({ theme, ...props }) => <List {...props} />)`
  dt {
    color: ${(props) => props.theme.palette.text.secondary};
    font-size: 8px;
  }

  dd {
    margin-left: 0;
    font-size: 12px;
  }

  .facility-details:hover {
    cursor: pointer;
  }
`;

const StyledGridCarrierDataItem = styled(Grid)`
  flex: 0 0 1;
`;

function UpsSpotLegInfo() {
  const theme = useTheme();
  const spotLegDetails = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLeg
  );
  const spotLegClaim = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLegClaim
  );
  const requirements = [];
  const driverType = types.spotDriverTypes.find(
    (x) => x.id === spotLegDetails.driverType
  );

  if (spotLegDetails.uiia) requirements.push("UIIA");
  if (spotLegDetails.doubles) requirements.push("Doubles");

  const getFacilityTooltip = (facilityDetails) => {
    if(facilityDetails) {
      return (
        <>
          {facilityDetails.slicCode} <br /> {facilityDetails.city},{" "}
          {facilityDetails.stateCode}{" "}
        </>
      )
    }
    else {
      return "";
    }
  };

  const getProposedArrivalDateTime = () => {
    const proposedPickupDateTime = utcToZonedDateTime(
      spotLegClaim.proposedPickupTimeUtc,
      spotLegDetails.movementOriginFacility.timeZoneName
    );
    const scheduledDepartureDateTime = utcToZonedDateTime(
      spotLegDetails.scheduledDepartureDateTimeUTC,
      spotLegDetails.movementOriginFacility.timeZoneName
    );

    const scheduledArrivalDateTime = utcToZonedDateTime(
      spotLegDetails.scheduledArrivalDateTimeUTC,
      spotLegDetails.movementDestinationFacility.timeZoneName
    );

    const timeDifference = differenceInMinutes(
      proposedPickupDateTime,
      scheduledDepartureDateTime
    );

    const proposedArrivalDateTime = addMinutes(
      scheduledArrivalDateTime,
      timeDifference
    );

    return proposedArrivalDateTime;
  };

  const getSortTypeCode = (sortTypeValue) => {
    const matchingSortType = Object.values(types.sortTypes).find(
      (type) => type.value === sortTypeValue
    );
    return matchingSortType?.code ?? "";
  };

  const serviceLevel = types.serviceLevels.find(
    (x) => x.id === spotLegDetails.serviceLevel
  );
  return (
    <>
      <StyledGridCarrierDataItem item>
        <StyledList component="dl" theme={theme}>
          <dt>Srv</dt>
          <dd className="facility-details">
            <Tooltip title={serviceLevel?.description ?? ""}>
              <span>{serviceLevel?.code}</span>
            </Tooltip>
          </dd>
        </StyledList>
      </StyledGridCarrierDataItem>
      <StyledGridCarrierDataItem item style={{ paddingLeft: 32 }}>
        <StyledList component="dl" theme={theme}>
          <dt>Leg Origin</dt>
          <dd className="facility-details">
            <Tooltip
              title={getFacilityTooltip(spotLegDetails.movementOriginFacility)}
            >
              <span>{spotLegDetails.movementOriginFacility.alphaCode}</span>
            </Tooltip>
          </dd>
        </StyledList>
      </StyledGridCarrierDataItem>
      <StyledGridCarrierDataItem item style={{ paddingLeft: 32 }}>
        <StyledList component="dl" theme={theme}>
          <dt>Sched Depart</dt>
          <dd>
            {formatTimeWithTimeZone(
              spotLegDetails.scheduledDepartureDateTimeUTC,
              spotLegDetails.movementOriginFacility.timeZoneName
            )}
            <br />
            {spotLegClaim?.proposedPickupTimeUtc && (
              <span
                style={{ fontSize: 12, color: theme.palette.primary.red }}
                data-testid="proposed-departure-schedule-change"
              >
                {formatTimeWithTimeZone(
                  spotLegClaim.proposedPickupTimeUtc,
                  spotLegDetails.movementOriginFacility.timeZoneName
                )}
              </span>
            )}
          </dd>
        </StyledList>
      </StyledGridCarrierDataItem>
      <StyledGridCarrierDataItem item style={{ paddingLeft: 32 }}>
        <StyledList component="dl" theme={theme}>
          <dt>Leg Dest</dt>
          <dd className="facility-details">
            <Tooltip
              title={getFacilityTooltip(
                spotLegDetails.movementDestinationFacility
              )}
            >
              <span>
                {spotLegDetails.movementDestinationFacility.alphaCode}
              </span>
            </Tooltip>
          </dd>
        </StyledList>
      </StyledGridCarrierDataItem>
      <StyledGridCarrierDataItem item style={{ paddingLeft: 32 }}>
        <StyledList component="dl" theme={theme}>
          <dt>Sched Arrival</dt>
          <dd>
            {formatTimeWithTimeZone(
              spotLegDetails.scheduledArrivalDateTimeUTC,
              spotLegDetails.movementDestinationFacility.timeZoneName
            )}
            <br />
            {spotLegClaim?.proposedPickupTimeUtc && (
              <span
                style={{ fontSize: 12, color: theme.palette.primary.red }}
                data-testid="proposed-arrival-schedule-change"
              >
                {tzFormat(getProposedArrivalDateTime(), "MM/dd HH:mm (z)", {
                  timeZone:
                    spotLegDetails.movementDestinationFacility.timeZoneName,
                })}
              </span>
            )}
          </dd>
        </StyledList>
      </StyledGridCarrierDataItem>
      <StyledGridCarrierDataItem item style={{ paddingLeft: 32 }}>
        <StyledList component="dl" theme={theme}>
          <dt>Miles</dt>
          <dd>{spotLegDetails.distance}</dd>
        </StyledList>
      </StyledGridCarrierDataItem>
      <StyledGridCarrierDataItem item style={{ paddingLeft: 32 }}>
        <StyledList component="dl" theme={theme}>
          <dt>Driver Type</dt>
          <dd>{driverType.title}</dd>
        </StyledList>
      </StyledGridCarrierDataItem>
      <StyledGridCarrierDataItem item style={{ paddingLeft: 32 }}>
        <StyledList component="dl" theme={theme}>
          <dt>Requirements</dt>
          <dd>{requirements?.length ? requirements.join(", ") : "None"}</dd>
        </StyledList>
      </StyledGridCarrierDataItem>
      <Grid container>
        <StyledGridCarrierDataItem item>
          <StyledList component="dl" theme={theme}>
            <dt>Load Origin</dt>
            <dd className="facility-details">
              <Tooltip
                title={getFacilityTooltip(spotLegDetails.loadOriginFacility)}
              >
                <span>
                  {spotLegDetails.loadOriginFacility?.alphaCode}{" "}
                  <SuperscriptText>
                    {getSortTypeCode(spotLegDetails.loadOriginSortType)}
                  </SuperscriptText>
                </span>
              </Tooltip>
            </dd>
          </StyledList>
        </StyledGridCarrierDataItem>
        <StyledGridCarrierDataItem item style={{ paddingLeft: 32 }}>
          <StyledList component="dl" theme={theme}>
            <dt>Load Dest</dt>
            <dd className="facility-details">
              <Tooltip
                title={getFacilityTooltip(
                  spotLegDetails.loadDestinationFacility
                )}
              >
                <span>
                  {spotLegDetails.loadDestinationFacility?.alphaCode}{" "}
                  <SuperscriptText>
                    {getSortTypeCode(spotLegDetails.loadDestinationSortType)}
                  </SuperscriptText>
                </span>
              </Tooltip>
            </dd>
          </StyledList>
        </StyledGridCarrierDataItem>
      </Grid>
    </>
  );
}

export default UpsSpotLegInfo;
